/**
 * Rechnung fuer die vereinte Komponenten-Liste des Feed-Checks.
 *
 * Sortiert wird nach PUNKTEVERLUST, nicht nach Rohwert: (100 - Wert) * Gewicht.
 * Damit steht der Titel mit 70 Punkten ueber der Kategorie mit 35, weil er
 * 25 statt 10 Prozent wiegt und den Feed mehr kostet. Das ist Absicht und
 * muss in der Oberflaeche durch die ausgewiesene Verlustzahl erklaert bleiben,
 * sonst wirkt die Reihenfolge falsch.
 *
 * Die Gewichte spiegeln SCORING_WEIGHTS aus supabase/functions/_shared/scoring.ts.
 * Aendern sie sich dort, muessen sie hier nachgezogen werden. Der Test
 * "spiegelt die Gewichte aus _shared/scoring.ts" liest die Edge-Datei per fs
 * und faengt eine Drift damit doch — Frontend und Edge Function koennen kein
 * Modul teilen, eine Datei lesen koennen sie beide.
 */

import { touchState, type TouchState } from './scoreTouch';

export type ComponentKey =
  | 'title' | 'description' | 'details' | 'highlights' | 'gpc' | 'attributes';

export const COMPONENT_WEIGHTS: Record<ComponentKey, number> = {
  title: 0.25,
  description: 0.2,
  attributes: 0.2,
  details: 0.15,
  highlights: 0.1,
  gpc: 0.1,
};

/** Wie viele Komponenten hoechstens als Baustelle angeboten werden. */
const MAX_WEAK = 3;

/**
 * Toleranz fuer den Widerspruchs-Test unten, in Score-Punkten.
 *
 * Die gespeicherten Spaltenmittel sind numeric und koennen gerundet sein; ein
 * Rundungsrest von Bruchteilen eines Punktes darf keinen Widerspruch behaupten.
 * Der reale Fall, um den es geht, liegt bei 16 Punkten (Feed
 * 6011578a-f12d-4cb1-8b1f-b4a16946d747: gewichtete Summe 52,47 gegen Note 68,67)
 * — eine halbe Punkt Toleranz trennt beides sauber und faellt lieber zugunsten
 * von "alles gemessen" aus.
 */
const INCONSISTENCY_TOLERANCE = 0.5;

/**
 * Toleranz fuer den Vorher/Nachher-Vergleich (`touch`), dieselbe halbe Punkt,
 * numeric-Rundung der Spaltenmittel wie `INCONSISTENCY_TOLERANCE`.
 */
export const FEED_TOUCH_TOLERANCE = INCONSISTENCY_TOLERANCE;

export interface BreakdownSnapshot {
  avg_score: number | string | null;
  avg_title_score: number | string | null;
  avg_description_score: number | string | null;
  avg_product_details_score: number | string | null;
  avg_highlights_score: number | string | null;
  avg_gpc_score: number | string | null;
  /**
   * Score v3: Mittel NUR ueber die Produkte MIT Attribut-Bucket. `null` heisst
   * dann "kein Produkt fuehrt Attribute", nicht "null Punkte" -- die
   * Unterscheidung traegt `attribute_applicable_count`, und dieses Feld darf
   * ohne sie nicht als Zahl gelesen werden.
   *
   * In einem Altbefund (vor v3) ist es das Mittel ueber ALLE Produkte, und
   * eine harte 0 dort heisst weiterhin "nie geschrieben" (siehe
   * `isInconsistent`).
   */
  avg_attribute_score: number | string | null;
  /**
   * Gemessene Grundgesamtheit des Befunds. Nenner des Anwendbarkeits-Anteils.
   * Optional, weil die Schnittstelle aelter ist als das Feld; fehlt sie oder
   * ist sie 0, gilt der Altbefund-Weg (Anteil 1).
   */
  product_count?: number | string | null;
  /**
   * Score v3: wie viele der `product_count` Produkte den Attribut-Bucket
   * ueberhaupt fuehren. `null`/fehlend = Altbefund, also kein Urteil ueber
   * Anwendbarkeit und damit Anteil 1.
   */
  attribute_applicable_count?: number | string | null;
  /**
   * Score v3: Mittel der PRO PRODUKT gerechneten Basiswerte, also vor dem
   * Vollstaendigkeits-Faktor. `null`/fehlend = Altbefund.
   *
   * Das ist die einzige Zahl, mit der die Vollstaendigkeits-Luecke bei
   * gemischter Anwendbarkeit exakt bleibt: aus den Spaltenmitteln allein ist
   * sie nicht rekonstruierbar (siehe `buildBreakdown`).
   */
  avg_base_score?: number | string | null;
  /**
   * `feed_health_snapshots.snapshot_type`. Nur fuer den Vergleich zweier
   * Befunde noetig (`touch`) -- optional, weil die Schnittstelle aelter ist
   * als der Vergleich und ein einzelner Befund keinen Typ braucht.
   */
  snapshot_type?: string | null;
  /**
   * `feed_health_snapshots.scoring_version`. Nur fuer den Vergleich zweier
   * Befunde noetig (`touch`): zwei Befunde unterschiedlicher Formel-Version
   * duerfen nie gegeneinander gehalten werden (siehe `comparable`).
   */
  scoring_version?: string | null;
}

export interface BreakdownRow {
  key: ComponentKey;
  /** Durchschnittswert der Komponente, 0 bis 100. Bei `measured: false` bedeutungslos. */
  score: number;
  /** Gewicht als Anteil, z.B. 0.25. */
  weight: number;
  /** Punkte, die diese Komponente den Feed aktuell kostet. Bei `measured: false` immer 0. */
  cost: number;
  isWeak: boolean;
  /**
   * false = der gespeicherte Befund traegt fuer diese Komponente keinen Wert.
   * Die 0 in der Spalte heisst dann "nicht gemessen", nicht "null Punkte", und
   * die Zeile darf weder als Baustelle noch als Punkteverlust auftreten.
   */
  measured: boolean;
  /**
   * Score v3: fuer welchen ANTEIL der gescorten Produkte diese Komponente
   * ueberhaupt bewertet wird, 0 bis 1.
   *
   * Nur die Attribut-Zeile kann hier unter 1 liegen; die uebrigen fuenf
   * Buckets fuehrt jede Kategorie (der Detail-Bucket kann edge-seitig zwar
   * ebenfalls entfallen, aber der Befund traegt dafuer keinen Zaehler, und
   * eine geratene Zahl waere schlechter als keine).
   *
   * 0 heisst "keine Kategorie dieses Feeds fuehrt das" -- die Zeile steht dann
   * in `notApplicable` und ihr `score` ist bedeutungslos.
   */
  applicableShare: number;
  /**
   * Hat die Optimierung diese Komponente angefasst? (Spec 2026-09-15, 2.3)
   *
   * `unknown` heisst: kein Vergleich moeglich -- kein Vorher-Befund uebergeben,
   * oder die beiden Befunde duerfen laut `comparable` nicht gegeneinander
   * gehalten werden (falsche Reihenfolge oder unterschiedliche/fehlende
   * Formel-Version).
   */
  touch: TouchState;
}

export interface Breakdown {
  /** Die teuersten GEMESSENEN Komponenten, absteigend nach cost. Hoechstens MAX_WEAK. */
  weak: BreakdownRow[];
  /** Der gemessene Rest, ebenfalls absteigend nach cost. */
  strong: BreakdownRow[];
  /** Komponenten ohne Messung, absteigend nach Gewicht. Im Normalfall leer. */
  unmeasured: BreakdownRow[];
  /**
   * Score v3: Komponenten, die fuer KEIN Produkt dieses Feeds gelten
   * (`applicableShare === 0`). Bei einem Altbefund immer leer.
   *
   * Ausdruecklich getrennt von `unmeasured`: dort heisst es "haetten gemessen
   * werden sollen, wurden aber nie geschrieben, die naechste Berechnung holt
   * das nach". Hier heisst es "gibt es fuer diese Kategorie nicht", und keine
   * Berechnung der Welt holt das nach. Zwei verschiedene Saetze, zwei
   * verschiedene Listen.
   */
  notApplicable: BreakdownRow[];
  /** Summe der Punkteverluste der GEMESSENEN Komponenten. */
  totalCost: number;
  /**
   * Was zusaetzlich zum Komponentenverlust fehlt. Entsteht durch den
   * Vollstaendigkeits-Faktor, der pro Produkt greift und aus Feed-Mittelwerten
   * nicht rekonstruierbar ist. Nie negativ.
   *
   * `null` heisst "nicht berechenbar", nicht "null Punkte": der Befund ist in
   * sich widerspruechlich (siehe `isInconsistent`), also gibt es keine ehrliche
   * Zahl. Die Oberflaeche laesst den Satz dann weg, statt eine 0 zu behaupten,
   * die der angezeigten Note widerspricht.
   */
  completenessGap: number | null;
}

/** Postgres numeric kommt ueber PostgREST als String; null gilt als 0. */
function toNumber(value: number | string | null): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Wie `toNumber`, aber `null` bleibt `null`.
 *
 * Fuer die drei v3-Felder ist genau das der Unterschied, um den es geht:
 * `toNumber(null) === 0` traegt die Aussage "null Punkte", und fuer
 * `attribute_applicable_count` hiesse das "kein Produkt fuehrt Attribute" --
 * eine Behauptung, die ein Altbefund nie gemacht hat.
 */
function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Der Anteil der Produkte, fuer die der Attribut-Bucket ueberhaupt gilt.
 *
 * 1 heisst "wie bisher" und ist der Rueckfall fuer JEDEN Fall, in dem der
 * Befund keine belastbare Aussage traegt: Altbefund ohne die Spalte, ein
 * Zaehler, der sich nicht als Zahl lesen laesst, oder eine Grundgesamtheit von
 * 0. Ein Anteil aus einer Division durch null waere keine Aussage, und "wir
 * wissen es nicht" darf hier nicht zu "nichts ist anwendbar" werden -- das
 * verschwiege die Attribut-Zeile auf jedem Feed mit leerem Zaehler.
 */
function applicableShareOf(snapshot: BreakdownSnapshot): number {
  const total = toNumberOrNull(snapshot.product_count);
  const applicable = toNumberOrNull(snapshot.attribute_applicable_count);
  if (total === null || applicable === null || total <= 0) return 1;
  /**
   * Geklemmt auf 0 bis 1, und das VERSCHWEIGT bewusst einen unmoeglichen
   * Befund: `attribute_applicable_count > product_count` kann es nicht geben
   * (die Teilmenge ist nie groesser als die Menge), und ein negativer Zaehler
   * erst recht nicht. Beides waere ein Schreiberfehler.
   *
   * Hier wird trotzdem nur geklemmt und nicht gemeldet. Zwei Gruende: diese
   * Funktion ist rein und laeuft im Render-Pfad (ein `console.warn` waere
   * gegen die Projektregel "kein console.log im Produktionscode" und feuerte
   * bei jedem Render erneut), und die Klemmung faellt in die sichere
   * Richtung -- nach oben auf 1, also auf das bisherige Verhalten mit voller
   * Attribut-Zeile. Ein zu grosser Zaehler laesst die Zeile stehen, statt sie
   * verschwinden zu lassen.
   *
   * Wer so einen Befund sucht, findet ihn an der Quelle: die beiden Zaehler
   * stehen in `feed_health_snapshots` nebeneinander und sind dort per SQL
   * pruefbar. Eine Zusicherung im Frontend waere die zweite Stelle fuer eine
   * Frage, die die Datenbank besser beantwortet.
   */
  return Math.min(1, Math.max(0, applicable / total));
}

const FIELD_BY_KEY: Record<ComponentKey, keyof BreakdownSnapshot> = {
  title: 'avg_title_score',
  description: 'avg_description_score',
  details: 'avg_product_details_score',
  highlights: 'avg_highlights_score',
  gpc: 'avg_gpc_score',
  attributes: 'avg_attribute_score',
};

/**
 * Ist der Befund arithmetisch unmoeglich?
 *
 * Fuer einen vollstaendig geschriebenen Befund gilt IMMER
 * `avg_score <= gewichtete Summe`. Beweis in zwei Schritten:
 *
 *   gewichtete Summe = Summe_c w_c * Mittel_i(s_ic)
 *                    = Mittel_i(Summe_c w_c * s_ic)   (Linearitaet)
 *                    = Mittel_i(Basiswert_i)
 *   avg_score        = Mittel_i(Basiswert_i * Faktor_i),  Faktor_i in [0,55; 1,00]
 *
 * Ein Faktor kleiner-gleich 1 kann den Mittelwert nur senken. Liegt die Note
 * trotzdem UEBER der gewichteten Summe, fehlt in der Summe mindestens ein
 * Summand — also eine nie geschriebene Spalte.
 *
 * Genau das ist der Bestand: 318 von 343 gespeicherten Befunden tragen bei
 * Produktdetails, Attributen und Google-Kategorie eine harte 0, weil der
 * Batch-Schreiber diese Spalten erst seit dem 2026-07-23 mitfuehrt. Die Spec
 * (Abschnitt 5) rechnete damit, dass jeder Feed vorher neu berechnet wird; der
 * Auto-Start greift aber nur, wenn GAR KEIN Befund existiert. Ein Feed mit
 * altem Befund behaelt seine Nullen, und ohne diesen Test bietet die Seite an,
 * genau die drei Bereiche zu beheben, die sie nie gemessen hat.
 *
 * Der Test ist bewusst kein Schwellenwert-Gefuehl ("0 wirkt zu niedrig"),
 * sondern eine Ungleichung, die nur bricht, wenn wirklich etwas fehlt. Ein
 * ehrlich verdientes 0-Mittel (Feed ohne einen einzigen Produktdetail-Eintrag)
 * senkt die Note mit und faellt deshalb NICHT durch diesen Test.
 */
function isInconsistent(avgScore: number, weightedBase: number): boolean {
  return avgScore > weightedBase + INCONSISTENCY_TOLERANCE;
}

/**
 * Der Renormierungs-Faktor der uebrigen fuenf Komponenten, Score v3.
 *
 * Edge-seitig (`_shared/scoring.ts:normFor`) wird der Basiswert JE PRODUKT
 * durch die Gewichtssumme der aktiven Buckets geteilt. Ein Produkt ohne
 * Attribut-Bucket hat damit den Nenner 0,8, und seine fuenf uebrigen
 * Komponenten wiegen entsprechend mehr. Dieselbe Rechnung im Feed-Mittel:
 *
 *   Anteil 1   -> Nenner 1,0   -> Faktor 1 (zeichengleich zum Altbefund)
 *   Anteil 0   -> Nenner 0,8   -> Faktor 1/0,8, und die Summe der Kosten ist
 *                                 wieder EXAKT `100 - Basiswert`
 *   dazwischen -> Nenner anteilig, und der Faktor ist eine NAEHERUNG
 *
 * Warum eine Naeherung: die Renormierung greift pro Produkt, das Mittel der
 * Quotienten ist aber nicht der Quotient der Mittel. Aus `avg_*` allein laesst
 * sich der wahre Mittelwert der Basiswerte nicht rekonstruieren -- dafuer
 * braeuchte es die Verteilung, nicht ihre Mittel. Gemessen an einer gemischten
 * Halbe-Halbe-Menge liegt die Abweichung bei rund 0,4 Punkten.
 *
 * Die VOLLSTAENDIGKEITS-LUECKE bleibt davon unberuehrt und exakt, weil sie aus
 * dem gespeicherten `avg_base_score` faellt und nicht aus dieser Rechnung. Die
 * Naeherung verteilt also nur den Verlust auf die Zeilen; die Aussage "so viel
 * fehlt insgesamt" stimmt weiter auf den Punkt.
 */
function otherWeightFactor(share: number): number {
  return 1 / (1 - COMPONENT_WEIGHTS.attributes * (1 - share));
}

/**
 * Darf `compare` als Vorher-Befund zu `snapshot` gelesen werden?
 *
 * Nur post gegen pre, und nur bei GLEICHER, bekannter Formel-Version. Das ist
 * die Regel aus baselineSnapshot.ts, hier noch einmal ausgeschrieben, weil sie
 * der Waechter gegen eine konkrete Regression ist: nach dem Sprung auf v3.1
 * traegt jeder Kunde einen v3.0-Pre-Befund mit GPC 100 und einen v3.1-Post-
 * Befund mit Tiefe. Ohne diese Bedingung stuende bei jedem "Kategorie nach
 * der Optimierung niedriger".
 */
function comparable(snapshot: BreakdownSnapshot, compare: BreakdownSnapshot | null | undefined): boolean {
  if (!compare) return false;
  if (snapshot.snapshot_type !== 'post' || compare.snapshot_type !== 'pre') return false;
  const v = snapshot.scoring_version ?? null;
  return v !== null && v === (compare.scoring_version ?? null);
}

export function buildBreakdown(
  snapshot: BreakdownSnapshot,
  compareSnapshot?: BreakdownSnapshot | null,
): Breakdown {
  const avgScore = toNumber(snapshot.avg_score);
  const canCompare = comparable(snapshot, compareSnapshot);
  const share = applicableShareOf(snapshot);
  const factor = otherWeightFactor(share);

  const raw = (Object.keys(COMPONENT_WEIGHTS) as ComponentKey[]).map((key) => {
    const isAttribute = key === 'attributes';
    // Bei Anteil 0 wird `avg_attribute_score` GAR NICHT gelesen: dort steht
    // `null`, und `toNumber` machte daraus eine 0, die sich von einem ehrlich
    // verdienten Nullwert nicht mehr unterscheiden liesse.
    const score = isAttribute && share === 0 ? 0 : toNumber(snapshot[FIELD_BY_KEY[key]]);
    return {
      key,
      score,
      weight: COMPONENT_WEIGHTS[key],
      applicableShare: isAttribute ? share : 1,
      // Das Gewicht, mit dem diese Zeile im renormierten Feed-Mittel wirkt.
      effectiveWeight: isAttribute ? COMPONENT_WEIGHTS[key] * share : COMPONENT_WEIGHTS[key] * factor,
    };
  });

  /**
   * Basiswert des Feeds, also der Stand VOR dem Vollstaendigkeits-Faktor.
   *
   * Bevorzugt der gespeicherte `avg_base_score` (v3): er ist das Mittel der pro
   * Produkt gerechneten Basiswerte und damit die einzige exakte Zahl. Fehlt er
   * (Altbefund), wird er aus den Spaltenmitteln mit denselben effektiven
   * Gewichten nachgerechnet -- bei Anteil 1 ist das die bisherige gewichtete
   * Summe, Zeichen fuer Zeichen, und in beiden Randfaellen gilt
   * `Summe der Kosten === 100 - Basiswert` exakt.
   */
  const weightedSum = raw.reduce((sum, r) => sum + r.score * r.effectiveWeight, 0);
  const weightedBase = toNumberOrNull(snapshot.avg_base_score) ?? weightedSum;
  const inconsistent = isInconsistent(avgScore, weightedBase);

  // Nur bei nachgewiesenem Widerspruch wird eine 0 als Messluecke gelesen.
  // Ohne diese Kopplung wuerde jede ehrlich verdiente 0 verschwinden, und die
  // Liste verschwiege die groesste Baustelle des Feeds.
  const rows: BreakdownRow[] = raw.map(({ effectiveWeight, ...r }) => {
    // Anwendbarkeit wird VOR der Messluecke entschieden: "gibt es hier nicht"
    // schlaegt "wurde nie geschrieben", und die 0 oben ist ein Platzhalter,
    // kein Messwert.
    if (r.applicableShare === 0) {
      return { ...r, cost: 0, isWeak: false, measured: true, touch: 'not_applicable' as const };
    }
    const measured = !(inconsistent && r.score === 0);
    // Fuer die Attribut-Zeile ausserdem: war der Bucket im VORHER-Befund gar
    // nicht anwendbar, ist ein Zahlvergleich sinnlos (Anteil-0-Wert steht dort
    // als Platzhalter 0, siehe der `share === 0`-Zweig oben) -- der Vergleich
    // bleibt dann `unknown` statt eine Verbesserung von 0 auf etwas zu lesen.
    const compareInapplicable = r.key === 'attributes' && canCompare
      && applicableShareOf(compareSnapshot as BreakdownSnapshot) === 0;
    const touch: TouchState = canCompare && measured && !compareInapplicable
      ? touchState(toNumber((compareSnapshot as BreakdownSnapshot)[FIELD_BY_KEY[r.key]]), r.score, FEED_TOUCH_TOLERANCE)
      : 'unknown';
    return {
      ...r,
      cost: measured ? (100 - r.score) * effectiveWeight : 0,
      isWeak: false,
      measured,
      touch,
    };
  });

  // Nach Gewicht, damit die groesste Luecke oben steht.
  const notApplicable = rows
    .filter((r) => r.applicableShare === 0)
    .sort((a, b) => b.weight - a.weight);
  const relevant = rows.filter((r) => r.applicableShare > 0);

  const measured = relevant.filter((r) => r.measured).sort((a, b) => b.cost - a.cost);
  const unmeasured = relevant.filter((r) => !r.measured).sort((a, b) => b.weight - a.weight);

  // Eine Komponente ohne Verlust ist keine Baustelle, auch wenn dadurch
  // weniger als MAX_WEAK uebrig bleiben.
  const weak = measured.slice(0, MAX_WEAK).filter((r) => r.cost > 0);
  weak.forEach((r) => { r.isWeak = true; });
  const strong = measured.filter((r) => !r.isWeak);

  const totalCost = measured.reduce((sum, r) => sum + r.cost, 0);
  const completenessGap = inconsistent ? null : Math.max(0, weightedBase - avgScore);

  return { weak, strong, unmeasured, notApplicable, totalCost, completenessGap };
}
